import { inject, Injectable, signal } from '@angular/core';
import { HttpParams, HttpRequest } from '@angular/common/http';
import { URL_AUTH_SIGNIN, URL_AUTH_PASSWORD_RESET, URL_AUTH_PASSWORD_FORGOT, URL_AUTH_TOKEN_REFRESH } from '@envs/base-urls';
import { BaseCustomizeUtilities } from '@domain/helpers/general/base-customize-utilities';
import { BaseUtilities } from '@shared/library/configs/helpers/base-utilities';
import { BaseApiResponseInterface } from '@core/interfaces/base-api-response';
import { TokenValidInterface } from '@core/interfaces/token-valid-interface';
import { UserApiInterface } from '@core/interfaces/user-api-interface';
import { UserInterface } from '@core/interfaces/user-interface';
import { userAdapter } from '@core/helpers/user-adapter';
import { BaseService } from '@core/server/base.service';
import { CookieService } from 'ngx-cookie-service';
import { map, Observable, shareReplay, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {
  public user = signal<UserInterface | undefined>( undefined );
  public isRefreshing = signal<boolean>( false );

  private readonly cookieSvc: CookieService = inject( CookieService );
  private readonly authChannel: BroadcastChannel = new BroadcastChannel('auth_channel');
  private refreshToken$: Observable<UserInterface | undefined> | null = null;

  private initialPage: string = BaseCustomizeUtilities.setInitialPage();

  public timerInactivityErrorRefreshingMS: number = 60000;

  constructor() {
    super();
    this.restoreSession();
  }

  public setRefreshing( value: boolean ){
    this.isRefreshing.set( value );
  }

  public addToken( request: HttpRequest<unknown> ) {
    const { userAccessToken, userRefreshToken, userTokenType } = this.getToken();
    const token = request.url === URL_AUTH_TOKEN_REFRESH ? userRefreshToken : userAccessToken;

    const clonedRequest = request.clone({
      setHeaders: {
        Authorization: `${userTokenType}${token}`
      }
    });

		return clonedRequest;
	}

  private restoreSession() {
    const storedUser: string = this.cookieSvc.get( 'user' );

    if ( storedUser ) this.user.set( JSON.parse( storedUser ) );
    else this.user.set(undefined);
  
    this.authChannel.onmessage = (event) => {
      if (event.data.type === 'logout') this.logOut();
    };  
  }

  public sign(username: string, password: string, handoff?: (user: UserInterface | undefined) => boolean) {
    this._http.post<BaseApiResponseInterface<UserApiInterface | undefined>>(URL_AUTH_SIGNIN, { username, password })
      .pipe(
        map((res: BaseApiResponseInterface<UserApiInterface | undefined>) => res.data),
        map((data: UserApiInterface | undefined): UserInterface | undefined => data ? userAdapter(data) : undefined),
        tap((data: UserInterface | undefined) => {
          this.setUser( data );
          if (handoff?.(data)) return;
          this._router.navigate(['/home']);
        })
      )
    .subscribe();
  }

  public refresh(): Observable<UserInterface | undefined> {
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    this.refreshToken$ = this._http.post<BaseApiResponseInterface<UserApiInterface | undefined>>(URL_AUTH_TOKEN_REFRESH, {})
      .pipe(
        map((res: BaseApiResponseInterface<UserApiInterface | undefined>) => res.data),
        map((data: UserApiInterface | undefined): UserInterface | undefined => data ? userAdapter(data) : undefined),
        shareReplay(1),
        tap({
          next: () => {
            this.refreshToken$ = null;
          },
          error: () => {
            this.refreshToken$ = null;
          }
        })
      );

    return this.refreshToken$;
  }

  public setUser( data:  UserInterface | undefined ) {
    this.user.set( data );
    this.cookieSvc.set( 'user', JSON.stringify(data), undefined, '/' );

    sessionStorage.removeItem('loggingOut');
  }

  public logOut() {
    location.replace('/');
    sessionStorage.setItem('loggingOut', 'true');

    this.deleteTokenSession();

    this._router.navigateByUrl('/login', { replaceUrl: true }).then(() => {
      sessionStorage.removeItem('loggingOut');
    });

    setTimeout(() => location.replace('/login'), 300);
  }

  private deleteTokenSession() {
    this.user.set(undefined);
    this.cookieSvc.set('pageActive', this.initialPage, undefined, '/');
    this.cookieSvc.delete('user');
    this.authChannel.postMessage({ type: 'logout' });
  }

  public forgotPassword(email: string): Observable<any> {
    const params: HttpParams = new HttpParams().set('email', email);
    return this._http.post(URL_AUTH_PASSWORD_FORGOT, null, { params });
  }

  public resetPassword(token: string, newPassword: string): Observable<any> {
    return this._http.post(URL_AUTH_PASSWORD_RESET, { token, newPassword });
  }

  public getToken(): TokenValidInterface {
    const sesionStorageUserItem: string = this.cookieSvc.get( 'user' );
    const isTokenValid: any | undefined = BaseUtilities.getValidObj( sesionStorageUserItem );

    const userAccessToken: string = isTokenValid ? JSON.parse(isTokenValid).accessToken : isTokenValid;
    const userRefreshToken: string = isTokenValid ? JSON.parse(isTokenValid).refreshToken : isTokenValid;
    const userTokenType: string = isTokenValid ? JSON.parse(isTokenValid).tokenType : isTokenValid;
  
    const objSend = { userAccessToken, userRefreshToken, userTokenType };
    return objSend;
  }

  public getUser(): UserInterface | undefined {
    const userSesion: string = this.cookieSvc.get( 'user' ) ?? '';
    return this.user() ?? JSON.parse( userSesion ); 
  }
}
